import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Container, Typography, Button, Paper, Divider } from '@mui/material';
import { ArrowLeft, ShieldCheck } from 'lucide-react';
import logoPainel from '../imagens/logopainelftm.png.ico';

const Section = ({ title, children }) => (
  <Box sx={{ mb: 4 }}>
    <Typography variant="h6" sx={{ color: '#fff', mb: 1.5, fontWeight: 700 }}>
      {title}
    </Typography>
    <Box sx={{ color: '#9CA3AF', lineHeight: 1.8, fontSize: '0.95rem' }}>
      {children}
    </Box>
  </Box>
);

const PoliticaPrivacidade = () => {
  const navigate = useNavigate();
  const updatedAt = '29 de abril de 2026';

  return (
    <Box sx={{ background: '#111827', minHeight: '100vh' }}>
      {/* Navbar */}
      <Box sx={{
        position: 'sticky', top: 0, zIndex: 1000,
        background: 'rgba(17, 24, 39, 0.85)', backdropFilter: 'blur(12px)',
        borderBottom: '1px solid rgba(124, 58, 237, 0.15)',
      }}>
        <Container maxWidth="lg">
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 1.5 }}>
            <Box
              sx={{ display: 'flex', alignItems: 'center', gap: 1.5, cursor: 'pointer' }}
              onClick={() => navigate('/')}
            >
              <img src={logoPainel} alt="FTM" style={{ width: 36, height: 36, borderRadius: 8 }} />
              <Typography sx={{ fontWeight: 800, fontSize: '1.1rem', color: '#fff' }}>
                PAINEL <span style={{ color: '#7C3AED' }}>FTM</span>
              </Typography>
            </Box>
            <Button
              variant="text"
              startIcon={<ArrowLeft size={18} strokeWidth={1.75} />}
              onClick={() => navigate('/')}
              sx={{ color: '#9CA3AF', fontWeight: 600, '&:hover': { color: '#fff' } }}
            >
              Voltar
            </Button>
          </Box>
        </Container>
      </Box>

      <Container maxWidth="md" sx={{ py: { xs: 5, sm: 8 } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
          <ShieldCheck size={28} strokeWidth={1.75} color="#7C3AED" />
          <Typography variant="h4" sx={{ color: '#fff', fontWeight: 800 }}>
            Política de Privacidade
          </Typography>
        </Box>
        <Typography sx={{ color: '#6B7280', mb: 4, fontSize: '0.9rem' }}>
          Última atualização: {updatedAt}
        </Typography>

        <Paper sx={{ p: { xs: 3, sm: 5 }, background: '#141926', border: '1px solid rgba(255,255,255,0.06)' }}>

          <Section title="1. Introdução">
            Esta Política de Privacidade descreve como o <strong style={{ color: '#fff' }}>Painel FTM</strong> ("nós",
            "nosso", "plataforma") coleta, utiliza, armazena, compartilha e protege os dados pessoais
            dos seus usuários ("você", "lead", "cliente"), em conformidade com a Lei Geral de Proteção
            de Dados Pessoais — <strong style={{ color: '#fff' }}>LGPD (Lei nº 13.709/2018)</strong>, o Marco Civil da Internet
            e demais legislações aplicáveis.
          </Section>

          <Section title="2. Dados que coletamos">
            Para criar e operar sua conta, coletamos:
            <ul style={{ marginTop: 8, paddingLeft: 20 }}>
              <li>Dados cadastrais: nome, e-mail, CPF, telefone;</li>
              <li>Dados de autenticação: senha (criptografada) ou credencial Google;</li>
              <li>Dados de uso: páginas acessadas, sites gerados, configurações do painel;</li>
              <li>Dados técnicos: endereço IP, navegador, sistema operacional, identificadores de dispositivo;</li>
              <li>Dados de pagamento (quando aplicável): informações necessárias para processar transações,
                tratadas por nossos parceiros de pagamento.</li>
            </ul>
          </Section>

          <Section title="3. Finalidade do tratamento">
            Utilizamos seus dados para:
            <ul style={{ marginTop: 8, paddingLeft: 20 }}>
              <li>Criar, autenticar e manter sua conta na plataforma;</li>
              <li>Gerar os sites/templates contratados e operar funcionalidades do painel;</li>
              <li>Cumprir obrigações legais, fiscais e regulatórias;</li>
              <li>Prevenir fraudes, abusos e violações dos Termos de Uso;</li>
              <li>Comunicar atualizações, suporte e — mediante seu consentimento — campanhas e novidades.</li>
            </ul>
          </Section>

          <Section title="4. Bases legais (LGPD)">
            O tratamento dos seus dados se baseia em: (i) execução de contrato; (ii) cumprimento de obrigação
            legal; (iii) legítimo interesse, sempre respeitando seus direitos e liberdades; e (iv) consentimento,
            quando exigido (ex.: comunicações de marketing e cookies não essenciais).
          </Section>

          <Section title="5. Cookies e tecnologias semelhantes">
            Utilizamos cookies essenciais (necessários para o funcionamento da plataforma) e cookies
            analíticos/marketing (mediante seu consentimento, capturado pelo banner de cookies). Você pode
            gerenciar suas preferências a qualquer momento limpando os dados do navegador ou alterando sua
            escolha no aviso de cookies.
          </Section>

          <Section title="6. Compartilhamento de dados">
            Não vendemos seus dados. Compartilhamos somente o necessário com:
            <ul style={{ marginTop: 8, paddingLeft: 20 }}>
              <li>Provedores de infraestrutura, hospedagem e e-mail;</li>
              <li>Gateways e processadores de pagamento;</li>
              <li>Ferramentas de analytics e marketing (com cookies aceitos);</li>
              <li>Autoridades públicas, quando exigido por lei ou ordem judicial.</li>
            </ul>
          </Section>

          <Section title="7. Armazenamento e segurança">
            Seus dados são armazenados em ambiente protegido, com medidas técnicas e administrativas
            razoáveis (criptografia em trânsito, controle de acesso, backups). Mantemos os dados pelo
            período necessário às finalidades informadas e/ou enquanto exigido por lei.
          </Section>

          <Section title="8. Seus direitos como titular (LGPD)">
            Você pode, a qualquer momento:
            <ul style={{ marginTop: 8, paddingLeft: 20 }}>
              <li>Confirmar a existência de tratamento;</li>
              <li>Acessar, corrigir, anonimizar ou eliminar dados desnecessários ou excessivos;</li>
              <li>Solicitar portabilidade dos dados;</li>
              <li>Revogar o consentimento;</li>
              <li>Opor-se a tratamentos baseados em legítimo interesse;</li>
              <li>Solicitar informações sobre compartilhamentos.</li>
            </ul>
            Para exercer seus direitos, fale conosco pelos canais oficiais de suporte do painel.
          </Section>

          <Divider sx={{ borderColor: 'rgba(255,255,255,0.06)', my: 4 }} />

          <Section title="9. Responsabilidade do usuário (lead/cliente)">
            Ao utilizar o Painel FTM, você declara, sob as penas da lei, que:
            <ul style={{ marginTop: 8, paddingLeft: 20 }}>
              <li>
                <strong style={{ color: '#fff' }}>Utilizará apenas dados, imagens, conteúdos e informações próprias</strong>
                {' '}— ou seja, dos quais detenha titularidade, autorização ou licença legítima — para gerar
                seus sites e configurar seu Business Manager (BM);
              </li>
              <li>
                <strong style={{ color: '#fff' }}>Não utilizará dados, documentos, identidades ou informações de terceiros</strong>
                {' '}sem autorização expressa, sendo o único e exclusivo responsável por qualquer uso indevido;
              </li>
              <li>Cumprirá integralmente as políticas da Meta/Facebook, do Google e demais plataformas
                onde aplicar o conteúdo gerado;</li>
              <li>Não utilizará a plataforma para atividades ilegais, fraudulentas, enganosas ou que
                infrinjam direitos de terceiros.</li>
            </ul>
          </Section>

          <Section title="10. Limitação de responsabilidade — Banimentos de BMs e contas">
            O Painel FTM é uma <strong style={{ color: '#fff' }}>ferramenta de geração de sites</strong> com templates que historicamente
            apresentam alta taxa de aprovação. Contudo:
            <ul style={{ marginTop: 8, paddingLeft: 20 }}>
              <li>
                <strong style={{ color: '#fff' }}>Não nos responsabilizamos por banimentos, suspensões, bloqueios, restrições
                ou qualquer ação adversa</strong> aplicada pela Meta (Facebook/Instagram), Google ou qualquer outra
                plataforma sobre Business Managers (BMs), contas de anúncio, perfis pessoais, páginas, domínios,
                contas de Google Ads ou demais ativos digitais do usuário;
              </li>
              <li>A aprovação ou reprovação da verificação de BM depende exclusivamente dos critérios da
                Meta, que podem ser alterados a qualquer momento, sem aviso prévio;</li>
              <li>Os resultados anteriores (incluindo estatísticas de aprovação divulgadas) não constituem
                garantia de resultado futuro;</li>
              <li>O usuário é o único responsável pelo conteúdo, dados e finalidade do uso dos sites
                gerados, bem como pelas campanhas, anúncios e operações realizadas em seus ativos digitais.</li>
            </ul>
          </Section>

          <Section title="11. Alterações desta Política">
            Podemos atualizar esta Política periodicamente. A versão vigente estará sempre disponível nesta
            página, com a respectiva data de atualização. Mudanças relevantes poderão ser comunicadas
            adicionalmente.
          </Section>

          <Section title="12. Contato">
            Em caso de dúvidas sobre esta Política ou sobre o tratamento dos seus dados pessoais, fale
            com nosso suporte através dos canais oficiais disponíveis no painel.
          </Section>

        </Paper>

        <Box sx={{ mt: 4, textAlign: 'center' }}>
          <Button
            variant="outlined"
            onClick={() => navigate('/')}
            startIcon={<ArrowLeft size={18} strokeWidth={1.75} />}
            sx={{
              borderColor: 'rgba(124, 58, 237, 0.4)', color: '#A78BFA',
              '&:hover': { borderColor: '#7C3AED', background: 'rgba(124, 58, 237, 0.08)' },
            }}
          >
            Voltar para o início
          </Button>
        </Box>
      </Container>
    </Box>
  );
};

export default PoliticaPrivacidade;
